/**
 * @file UserRowActions.tsx
 * @description Действия в строке пользователя (phase08.5 shadcn).
 */

import { Pencil, ShieldUser, Trash2, UserCog } from "lucide-react";
import { useState } from "react";

import { useAuth, useHasPermission } from "../../auth/hooks";
import { ImpersonationStartModal } from "../../components/ImpersonationStartModal";
import { UserContextsEditor } from "../../components/UserContextsEditor";
import { UserEditModal } from "../../components/UserEditModal";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { useNotify } from "../../components/ui/NotifyProvider";
import { Switch } from "../../components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import {
  hardDeleteAdminUser,
  type AdminUser,
} from "../../lib/api-admin-users";
import { ApiError } from "../../lib/api-client";

interface UserRowActionsProps {
  user: AdminUser;
  shops: Array<{ id: string; slug: string; displayName: string }>;
  onToggle: (u: AdminUser) => void;
  onContextChanged: (u: AdminUser) => void;
  onDeleted?: (userId: string) => void;
}

export function UserRowActions({
  user,
  shops,
  onToggle,
  onContextChanged,
  onDeleted,
}: UserRowActionsProps) {
  const [editorOpen, setEditorOpen] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [impersonateOpen, setImpersonateOpen] = useState(false);
  const notify = useNotify();

  const canImpersonate = useHasPermission("PLATFORM_IMPERSONATE_USER");
  const canManagePlatform = useHasPermission("PLATFORM_MANAGE_USERS");
  const { me } = useAuth();
  const isSelf = me?.user.id === user.id;
  const targetHasOnlyPlatform = user.contexts.every((c) => c.contextType === "PLATFORM");
  const impersonationAllowed = canImpersonate && !isSelf && !targetHasOnlyPlatform;
  const canEdit = canManagePlatform;
  const canDelete = canManagePlatform && !isSelf;

  async function onConfirmDelete() {
    setDeleting(true);
    try {
      await hardDeleteAdminUser(user.id);
      onDeleted?.(user.id);
      setDeleteOpen(false);
      notify.success(`Пользователь ${user.email} удалён`);
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось удалить пользователя");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <div className="flex items-center justify-end gap-1">
        {impersonationAllowed ? (
          <Tooltip>
            <TooltipTrigger
              render={(props) => (
                <Button
                  {...props}
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setImpersonateOpen(true)}
                  aria-label="Войти как пользователь"
                >
                  <UserCog className="size-4" />
                </Button>
              )}
            />
            <TooltipContent>Войти как пользователь</TooltipContent>
          </Tooltip>
        ) : null}

        {canEdit ? (
          <Tooltip>
            <TooltipTrigger
              render={(props) => (
                <Button
                  {...props}
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setEditProfileOpen(true)}
                  aria-label="Редактировать пользователя"
                >
                  <Pencil className="size-4" />
                </Button>
              )}
            />
            <TooltipContent>Редактировать (имя, пароль)</TooltipContent>
          </Tooltip>
        ) : null}

        <Tooltip>
          <TooltipTrigger
            render={(props) => (
              <Button
                {...props}
                variant="ghost"
                size="icon-sm"
                onClick={() => setEditorOpen(true)}
                aria-label="Контексты пользователя"
              >
                <ShieldUser className="size-4" />
              </Button>
            )}
          />
          <TooltipContent>Контексты пользователя</TooltipContent>
        </Tooltip>

        {canDelete ? (
          <Tooltip>
            <TooltipTrigger
              render={(props) => (
                <Button
                  {...props}
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setDeleteOpen(true)}
                  aria-label="Удалить пользователя"
                  className="text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="size-4" />
                </Button>
              )}
            />
            <TooltipContent>Удалить пользователя</TooltipContent>
          </Tooltip>
        ) : null}

        <Tooltip>
          <TooltipTrigger
            render={(props) => (
              <span {...props}>
                <Switch
                  checked={user.isActive}
                  onCheckedChange={() => onToggle(user)}
                  aria-label={user.isActive ? "Деактивировать" : "Активировать"}
                />
              </span>
            )}
          />
          <TooltipContent>
            {user.isActive ? "Активен" : "Деактивирован"}
          </TooltipContent>
        </Tooltip>
      </div>

      <Dialog open={editorOpen} onOpenChange={(o) => !o && setEditorOpen(false)}>
        <DialogContent fullScreen className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Контексты: {user.email}</DialogTitle>
          </DialogHeader>
          <UserContextsEditor
            user={user}
            scope={{ scope: "platform", shops }}
            onChanged={(u) => {
              onContextChanged(u);
              setEditorOpen(false);
            }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>
              Закрыть
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UserEditModal
        open={editProfileOpen}
        user={user}
        onClose={() => setEditProfileOpen(false)}
        onSaved={(u) => {
          onContextChanged(u);
          setEditProfileOpen(false);
        }}
      />

      <Dialog open={deleteOpen} onOpenChange={(o) => !o && !deleting && setDeleteOpen(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Удалить пользователя?</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3 text-sm">
            <p>
              Учётная запись <strong>{user.email}</strong> будет удалена
              безвозвратно вместе со всеми её контекстами и токенами сброса
              пароля.
            </p>
            <p className="text-on-surface-variant">
              Если у пользователя есть связанные бизнес-данные (заказы,
              сделки, аудит-логи) — удаление не выполнится. В этом случае
              используйте деактивацию (переключатель в строке).
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteOpen(false)}
              disabled={deleting}
            >
              Отмена
            </Button>
            <Button
              // Окончательное подтверждение удаления — сплошная красная.
              variant="destructive-solid"
              onClick={() => void onConfirmDelete()}
              disabled={deleting}
            >
              {deleting ? "Удаляем…" : "Удалить безвозвратно"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImpersonationStartModal
        open={impersonateOpen}
        user={user}
        onClose={() => setImpersonateOpen(false)}
        onStarted={() => {
          setImpersonateOpen(false);
          window.location.assign("/admin/");
        }}
      />
    </>
  );
}
